import { useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import type { CanvasGroup, GroupBounds } from '../../types';
import { getAttachedResourceIds } from '../../entityTypes';
import { useGroupBoxInteraction } from './useGroupBoxInteraction';
import { useCanvasStores } from '../../stores/CanvasContext';
import { ResourceAggregationFooter } from '../../components/ResourceAggregationFooter';

const BORDER_HIT = 6; // px clickable border zone

export interface CanvasGroupBoxExpandedProps {
  group: CanvasGroup;
  bounds: GroupBounds;
}

export function CanvasGroupBoxExpanded({ group, bounds }: CanvasGroupBoxExpandedProps) {
  const {
    isSelected, isEntered, borderColor, borderWidth,
    handlePointerDown, handleClick, handleDoubleClick, handleContextMenu, handleCollapseClick,
  } = useGroupBoxInteraction(group.id, group.layerId, group.borderColor, group.borderWidth);

  const displayMode = group.displayMode;

  const { store } = useCanvasStores();

  // Check for attached resources among group members
  const { hasResourceBadges, allEntities, memberEntityIds } = useMemo(() => {
    const ctx = store.getSnapshot().context;
    const entities = [
      ...Object.values(ctx.nodes),
      ...Object.values(ctx.groups),
    ];
    const members = group.memberEntityIds;
    const memberSet = new Set(members);
    const hasRB = entities.some(e => memberSet.has(e.id) && getAttachedResourceIds(e).length > 0);
    return { hasResourceBadges: hasRB, allEntities: entities, memberEntityIds: members };
  }, [store, group]);

  // ── Invisible mode ──
  if (displayMode === 'invisible') {
    // Not selected, not entered → completely invisible, no interaction
    if (!isSelected && !isEntered) {
      return (
        <div
          data-canvas-entity
          data-canvas-group={group.id}
          style={{
            position: 'absolute',
            left: bounds.x,
            top: bounds.y,
            width: bounds.width,
            height: bounds.height,
            pointerEvents: 'none',
          }}
        />
      );
    }

    // Selected or entered → show dashed border
    const dashColor = isEntered ? '#22c55e' : '#3b82f6';
    const bgColor = isEntered ? 'rgba(34,197,94,0.03)' : 'rgba(59,130,246,0.03)';

    return (
      <div
        data-canvas-entity
        data-canvas-group={group.id}
        style={{
          position: 'absolute',
          left: bounds.x,
          top: bounds.y,
          width: bounds.width,
          height: bounds.height,
          pointerEvents: 'none',
          border: `2px dashed ${dashColor}`,
          borderRadius: 8,
          backgroundColor: bgColor,
        }}
      >
        {/* Label badge for entered state */}
        {isEntered && (
          <div
            style={{
              position: 'absolute',
              top: -10,
              left: 8,
              fontSize: 9,
              fontWeight: 700,
              color: '#22c55e',
              backgroundColor: '#f0fdf4',
              padding: '1px 6px',
              borderRadius: 4,
              border: '1px solid #bbf7d0',
              pointerEvents: 'auto',
              cursor: 'default',
              userSelect: 'none',
            }}
            onContextMenu={handleContextMenu}
          >
            {group.label} (EDIT)
          </div>
        )}
      </div>
    );
  }

  // ── Container mode (existing behaviour) ──
  return (
    <div
      data-canvas-entity
      data-canvas-group={group.id}
      style={{
        position: 'absolute',
        left: bounds.x,
        top: bounds.y,
        width: bounds.width,
        height: bounds.height,
        pointerEvents: 'none', // body passes through
        backgroundColor: group.backgroundColor,
        borderRadius: 8,
      }}
    >
      {/* Top border zone (includes header) */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 28,
          borderTop: `${borderWidth}px solid ${borderColor}`,
          borderLeft: `${borderWidth}px solid ${borderColor}`,
          borderRight: `${borderWidth}px solid ${borderColor}`,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          pointerEvents: 'auto',
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '0 8px',
          userSelect: 'none',
          backgroundColor: 'rgba(248, 250, 252, 0.9)',
        }}
        onPointerDown={handlePointerDown}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
      >
        <button
          data-group-collapse-btn
          onClick={handleCollapseClick}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', color: '#64748b' }}
        >
          <ChevronDown size={14} />
        </button>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#334155' }}>
          {group.label}
        </span>
        {group.description && (
          <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 4 }}>
            {group.description}
          </span>
        )}
        {isEntered && (
          <span style={{
            fontSize: 9,
            fontWeight: 700,
            color: '#22c55e',
            backgroundColor: '#f0fdf4',
            padding: '1px 6px',
            borderRadius: 4,
            marginLeft: 'auto',
          }}>
            EDIT
          </span>
        )}
      </div>

      {/* Left border */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 0,
          width: BORDER_HIT,
          bottom: 0,
          borderLeft: `${borderWidth}px solid ${borderColor}`,
          borderBottomLeftRadius: 8,
          pointerEvents: 'auto',
          cursor: 'grab',
        }}
        onPointerDown={handlePointerDown}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      />

      {/* Right border */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          right: 0,
          width: BORDER_HIT,
          bottom: 0,
          borderRight: `${borderWidth}px solid ${borderColor}`,
          borderBottomRightRadius: 8,
          pointerEvents: 'auto',
          cursor: 'grab',
        }}
        onPointerDown={handlePointerDown}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      />

      {/* Bottom border */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: BORDER_HIT,
          borderBottom: `${borderWidth}px solid ${borderColor}`,
          borderLeft: `${borderWidth}px solid ${borderColor}`,
          borderRight: `${borderWidth}px solid ${borderColor}`,
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
          pointerEvents: 'auto',
          cursor: 'grab',
        }}
        onPointerDown={handlePointerDown}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      />

      {/* Resource Aggregation Footer */}
      {hasResourceBadges && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 24,
            borderTop: `1px solid ${borderColor}`,
            borderBottom: `${borderWidth}px solid ${borderColor}`,
            borderLeft: `${borderWidth}px solid ${borderColor}`,
            borderRight: `${borderWidth}px solid ${borderColor}`,
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            backgroundColor: 'rgba(248, 250, 252, 0.9)',
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            padding: '0 8px',
            gap: 4,
            fontSize: 9,
            color: '#64748b',
          }}
        >
          <ResourceAggregationFooter memberEntityIds={memberEntityIds} allEntities={allEntities} />
        </div>
      )}
    </div>
  );
}
